if (!customElements.get('mix-match-builder')) {
  customElements.define(
    'mix-match-builder',
    class MixMatchBuilder extends HTMLElement {
      connectedCallback() {
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.selections = new Map();
        this.storageKey = this.dataset.storageKey || 'mix-match';
        this.moneyFormatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });

        this.restore();

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onInput.bind(this));

        const search = this.querySelector('[data-mm-search]');
        if (search) {
          search.addEventListener(
            'input',
            debounce(() => this.filterCards(search.value), 200)
          );
        }

        this.registerSwym();
        this.syncCards();
        this.render();

        // Back/forward should restore the matching filtered view.
        this.onPopState = () => this.refreshCatalog(new URLSearchParams(window.location.search), false);
        window.addEventListener('popstate', this.onPopState);

        // How deep into "Load more" the customer currently is (1 = first page).
        this.loadedPages = 1;
        this.onPageHide = () => this.persistNav();
        window.addEventListener('pagehide', this.onPageHide);

        this.initStickyBar();
        this.maybeRestoreView();
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        window.removeEventListener('pagehide', this.onPageHide);
        if (this.onBarScroll) {
          window.removeEventListener('scroll', this.onBarScroll);
          window.removeEventListener('resize', this.onBarScroll);
        }
        if (this.stickyHeader && this.syncBarTop) {
          this.stickyHeader.removeEventListener('transitionend', this.syncBarTop);
        }
      }

      /* Keep the sticky selection bar flush under the theme header. The header
       * hides on scroll-down and reveals on scroll-up, so a constant top offset
       * (var(--header-height)) strands the bar mid-page. Instead, track the
       * header's live bottom edge — 0 when it's slid off-screen. */
      initStickyBar() {
        this.mmBar = this.querySelector('[data-mm-bar]');
        if (!this.mmBar) return;
        this.stickyHeader = document.querySelector('.section-header');

        let ticking = false;
        this.syncBarTop = () => {
          ticking = false;
          const bottom = this.stickyHeader ? this.stickyHeader.getBoundingClientRect().bottom : 0;
          this.mmBar.style.top = `${Math.max(0, Math.round(bottom))}px`;
        };
        this.onBarScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(this.syncBarTop);
        };
        window.addEventListener('scroll', this.onBarScroll, { passive: true });
        window.addEventListener('resize', this.onBarScroll, { passive: true });
        // The header slides via CSS transition; catch its final resting position.
        if (this.stickyHeader) this.stickyHeader.addEventListener('transitionend', this.syncBarTop);
        this.syncBarTop();
      }

      /* --- state --- */

      /* Pack sizes are milestones, not targets: nothing caps the basket, so the
       * tier a customer has *reached* is derived from the live count rather than
       * chosen up front. Sorted ascending so "achieved" and "next" are simple
       * scans. */
      get tiers() {
        return [...this.querySelectorAll('[data-mm-tier]')]
          .map((el) => ({ el, size: parseInt(el.dataset.quantity, 10), label: el.dataset.label }))
          .filter((tier) => Number.isFinite(tier.size) && tier.size > 0)
          .sort((a, b) => a.size - b.size);
      }

      // Highest milestone the count has reached, or null below the smallest.
      achievedTier(total = this.totalCount) {
        let achieved = null;
        this.tiers.forEach((tier) => {
          if (total >= tier.size) achieved = tier;
        });
        return achieved;
      }

      // Smallest milestone still out of reach, or null once past the largest.
      nextTier(total = this.totalCount) {
        return this.tiers.find((tier) => total < tier.size) || null;
      }

      get totalCount() {
        let total = 0;
        this.selections.forEach((entry) => (total += entry.qty));
        return total;
      }

      get subtotalCents() {
        let total = 0;
        this.selections.forEach((entry) => (total += entry.qty * entry.price));
        return total;
      }

      persist() {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify([...this.selections]));
        } catch (_) {
          // Storage unavailable (private mode) — selections just won't survive a reload.
        }
      }

      restore() {
        try {
          const stored = JSON.parse(sessionStorage.getItem(this.storageKey));
          if (Array.isArray(stored)) {
            stored.forEach(([id, entry]) => {
              if (entry && entry.qty > 0) this.selections.set(String(id), entry);
            });
          }
        } catch (_) {
          // Ignore corrupt state.
        }
      }

      /* --- restoring the browsing position on back-navigation ---
       *
       * The catalog is paginated, so a customer three "Load more" pages deep who
       * opens a drink and comes back lands on a page that is only one grid tall.
       * Restoring the scroll offset alone cannot work — the pages have to come
       * back first. So we remember how deep they were, re-fetch those pages, and
       * only then scroll. */

      get navKey() {
        return `${this.storageKey}:nav`;
      }

      // Identity of the view being remembered: path plus filter params. Excludes
      // `page`/`section_id` so a restore isn't defeated by our own fetch params.
      viewKey() {
        const params = new URLSearchParams(window.location.search);
        params.delete('section_id');
        params.delete('page');
        const qs = params.toString();
        return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      }

      persistNav() {
        try {
          sessionStorage.setItem(
            this.navKey,
            JSON.stringify({ view: this.viewKey(), pages: this.loadedPages, scrollY: Math.round(window.scrollY) })
          );
        } catch (_) {
          // Storage unavailable (private mode) — position just won't come back.
        }
      }

      maybeRestoreView() {
        // Only after a real back/forward. Arriving fresh from a link or the menu
        // should start at the top, not silently resurrect an old position.
        // (A bfcache restore never gets here — the DOM and scroll are preserved
        // natively and connectedCallback does not re-run.)
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type !== 'back_forward') return;

        let stored = null;
        try {
          stored = JSON.parse(sessionStorage.getItem(this.navKey));
        } catch (_) {
          return;
        }
        if (!stored || stored.view !== this.viewKey()) return;
        if (!(stored.pages > 1) && !(stored.scrollY > 0)) return;

        // Take scroll restoration off the browser: it would fire now, long
        // before the extra pages exist, and land in the wrong place.
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        this.restorePages(stored.pages).then(() => {
          requestAnimationFrame(() => window.scrollTo(0, stored.scrollY));
        });
      }

      restorePages(pages) {
        const grid = this.querySelector('[data-mm-grid]');
        if (!grid || !(pages > 1) || !this.dataset.collectionUrl) return Promise.resolve();

        const base = new URLSearchParams(window.location.search);
        base.delete('section_id');
        base.delete('page');

        const wanted = [];
        for (let page = 2; page <= pages; page += 1) wanted.push(page);

        this.classList.add('mix-match--loading');
        return Promise.all(
          wanted.map((page) => {
            const params = new URLSearchParams(base);
            params.set('page', String(page));
            params.set('section_id', this.dataset.sectionId);
            return fetch(`${this.dataset.collectionUrl}?${params}`)
              .then((response) => response.text())
              .then((text) => new DOMParser().parseFromString(text, 'text/html'))
              .catch(() => null);
          })
        )
          .then((docs) => {
            // Promise.all preserves input order, so this appends in page order
            // however the requests settled. Stop at the first failure rather
            // than leaving a hole — a gap would also make "Load more" skip
            // products, which is worse than restoring less far.
            let lastDoc = null;
            for (const doc of docs) {
              if (!doc) break;
              doc.querySelectorAll('[data-mm-grid] > *').forEach((card) => {
                grid.appendChild(document.importNode(card, true));
              });
              lastDoc = doc;
              this.loadedPages += 1;
            }

            // Hand "Load more" the next URL after the deepest page restored.
            const button = this.querySelector('[data-mm-load-more]');
            const nextButton = lastDoc ? lastDoc.querySelector('[data-mm-load-more]') : null;
            if (button && nextButton) {
              button.dataset.nextUrl = nextButton.dataset.nextUrl;
            } else if (button && lastDoc) {
              button.closest('.mix-match__load-more').hidden = true;
            }

            const search = this.querySelector('[data-mm-search]');
            if (search && search.value) this.filterCards(search.value);
            this.syncCards();
          })
          .finally(() => this.classList.remove('mix-match--loading'));
      }

      /* --- events --- */

      onClick(event) {
        const stepUp = event.target.closest('[data-mm-plus], [data-mm-add]');
        if (stepUp && this.contains(stepUp)) return this.step(stepUp, 1);

        const stepDown = event.target.closest('[data-mm-minus]');
        if (stepDown) return this.step(stepDown, -1);

        const remove = event.target.closest('[data-mm-remove]');
        if (remove) return this.removeSelection(remove.dataset.variantId);

        const loadMore = event.target.closest('[data-mm-load-more]');
        if (loadMore) return this.loadMore(loadMore);

        const chip = event.target.closest('[data-mm-filter-chip]');
        if (chip) {
          chip.classList.toggle('mix-match-chip--active');
          chip.setAttribute('aria-pressed', chip.classList.contains('mix-match-chip--active'));
          return this.applyFilters();
        }

        const clear = event.target.closest('[data-mm-filter-clear]');
        if (clear) {
          this.querySelectorAll(`[data-mm-filter-chip][data-param-name="${clear.dataset.paramName}"]`).forEach((c) => {
            c.classList.remove('mix-match-chip--active');
            c.setAttribute('aria-pressed', 'false');
          });
          return this.applyFilters();
        }

        const filterToggle = event.target.closest('[data-mm-filter-toggle]');
        if (filterToggle) return this.toggleFilterGroup(filterToggle);

        const submit = event.target.closest('[data-mm-submit]');
        if (submit) return this.addBundleToCart(submit);
      }

      onChange(event) {
        if (event.target.matches('[data-mm-variant-select]')) this.onVariantChange(event.target);
        // Delegated: the select is inside [data-mm-catalog] and is replaced on
        // every refresh, so it can't hold its own listener.
        if (event.target.matches('[data-mm-sort]')) this.applyFilters();
      }

      onInput(event) {
        // Delegated so it survives the catalog getting replaced on filter/load-more.
        // The main product search ([data-mm-search]) is bound separately — ignore it here.
        if (event.target.matches('[data-mm-filter-search]')) this.filterChips(event.target);
      }

      /* --- filter-group collapse / in-group search --- */

      toggleFilterGroup(toggle) {
        const group = toggle.closest('[data-mm-filter-group]');
        if (!group) return;
        const expanded = group.classList.toggle('mix-match-filters__group--expanded');
        toggle.setAttribute('aria-expanded', String(expanded));
        const more = toggle.querySelector('[data-mm-filter-toggle-more]');
        const less = toggle.querySelector('[data-mm-filter-toggle-less]');
        if (more) more.hidden = expanded;
        if (less) less.hidden = !expanded;
      }

      filterChips(input) {
        const group = input.closest('[data-mm-filter-group]');
        if (!group) return;
        const term = (input.value || '').trim().toLowerCase();
        group.classList.toggle('mix-match-filters__group--searching', term.length > 0);
        group.querySelectorAll('[data-mm-filter-chip]').forEach((chip) => {
          const label = chip.dataset.mmFilterLabel || chip.textContent.trim().toLowerCase();
          chip.classList.toggle('mix-match-chip--filtered-out', term.length > 0 && !label.includes(term));
        });
      }

      /* --- selection --- */

      cardMeta(card) {
        const select = card.querySelector('[data-mm-variant-select]');
        const option = select ? select.options[select.selectedIndex] : null;
        return {
          variantId: card.dataset.variantId,
          price: parseInt(option ? option.dataset.price : card.dataset.price, 10) || 0,
          max: this.parseMax(option ? option.dataset.max : card.dataset.max),
          title: card.dataset.title,
          variantTitle: option ? option.dataset.variantTitle : '',
          image: card.dataset.image,
          url: card.dataset.url,
        };
      }

      parseMax(value) {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? Infinity : parsed;
      }

      step(button, delta) {
        const card = button.closest('[data-mm-card]');
        if (!card) return;
        const meta = this.cardMeta(card);
        const current = this.selections.get(meta.variantId)?.qty || 0;

        // Only real inventory limits the basket — pack sizes do not.
        const next = Math.max(0, Math.min(current + delta, meta.max));

        if (next === 0) {
          this.selections.delete(meta.variantId);
        } else {
          this.selections.set(meta.variantId, { ...meta, qty: next });
        }

        this.persist();
        this.render();

        if (delta > 0 && next > current) {
          const stepper = card.querySelector('[data-mm-stepper]');
          if (button.hasAttribute('data-mm-add') && stepper) {
            const plus = stepper.querySelector('[data-mm-plus]');
            (plus && !plus.disabled ? plus : stepper.querySelector('[data-mm-minus]'))?.focus();
          }
        }
      }

      removeSelection(variantId) {
        if (!variantId) return;
        this.selections.delete(String(variantId));
        this.persist();
        this.render();
      }

      onVariantChange(select) {
        const card = select.closest('[data-mm-card]');
        if (!card) return;
        card.dataset.variantId = select.value;
        this.updateCard(card);
      }

      /* --- rendering --- */

      formatMoney(cents) {
        return this.moneyFormatter.format(cents / 100);
      }

      fillTemplate(template, values) {
        let text = template || '';
        Object.keys(values).forEach((key) => {
          text = text.replaceAll(`[${key}]`, values[key]);
        });
        return text;
      }

      updateCard(card) {
        const meta = this.cardMeta(card);
        const qty = this.selections.get(meta.variantId)?.qty || 0;

        card.classList.toggle('mix-match-card--selected', qty > 0);

        const addButton = card.querySelector('[data-mm-add]');
        const stepper = card.querySelector('[data-mm-stepper]');
        if (addButton && !addButton.disabled) addButton.hidden = qty > 0;
        if (stepper) {
          stepper.hidden = qty === 0;
          stepper.querySelector('[data-mm-qty]').textContent = qty;
          stepper.querySelector('[data-mm-plus]').disabled = qty >= meta.max;
          stepper.querySelector('[data-mm-minus]').disabled = qty === 0;
        }

        const countBadge = card.querySelector('[data-mm-card-count]');
        if (countBadge) {
          countBadge.hidden = qty === 0;
          countBadge.textContent = `×${qty}`;
        }

        const stockHint = card.querySelector('[data-mm-stock-hint]');
        if (stockHint) {
          const threshold = parseInt(this.dataset.lowStockThreshold, 10) || 0;
          const showHint = threshold > 0 && meta.max !== Infinity && meta.max > 0 && meta.max <= threshold;
          stockHint.hidden = !showHint;
          if (showHint) {
            stockHint.textContent = this.fillTemplate(this.dataset.textLowStock, { count: meta.max });
          }
        }
      }

      syncCards() {
        this.querySelectorAll('[data-mm-card]').forEach((card) => this.updateCard(card));
        // Filtering and "Load more" replace the whole catalog, so newly inserted
        // sold-out cards need their Swym buttons bound too. Doing it here covers
        // every render path in one place.
        this.bindNotifyButtons();
      }

      /* --- Swym "notify me when back in stock" on sold-out cards (item 7) ---
       *
       * Swym only auto-binds its button on product detail pages (it looks for the
       * product form). On a collection grid the theme owns the whole interaction:
       * per Swym's "Enabling Back in Stock Alert Buttons on Collections Pages"
       * guide we attach the click handler ourselves AND add the `swym-loaded`
       * class — that class is what reveals the button, since our CSS hides
       * `.mix-match-card__notify:not(.swym-loaded)`. So the button can only ever
       * appear if it is genuinely wired up; otherwise the disabled "Sold out"
       * button remains visible, exactly as before this feature existed.
       */
      registerSwym() {
        if (this.swymRegistered) return;
        this.swymRegistered = true;
        this.notifyHandler = this.onNotifyClick.bind(this);
        // SwymCallbacks is a queue drained once Swym is ready, so pushing before
        // or after its script loads both work.
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push((swat) => {
          this.swat = swat;
          this.bindNotifyButtons();
        });
      }

      bindNotifyButtons() {
        if (!this.swat || !this.isConnected) return;
        this.querySelectorAll('[data-mm-notify]:not(.swym-loaded)').forEach((button) => {
          button.addEventListener('click', this.notifyHandler);
          button.classList.add('swym-loaded');
        });
      }

      onNotifyClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const params = {
          empi: Number(button.dataset.productId),
          epi: Number(button.dataset.variantId),
          du: button.dataset.url,
        };
        if (!params.empi || !params.epi) return;

        // Swym needs price + image on the subscription, which it reads back from
        // the product JSON rather than trusting the DOM.
        this.swat.getProductDetails(params, (productJSON) => {
          const variant = (productJSON?.variants || []).find((v) => String(v.id) === String(params.epi));
          if (!variant) return;
          params.pr = window.SwymUtils ? window.SwymUtils.formatProductPrice(variant.price) : variant.price;
          params.iu = productJSON.featured_image;
          params.et = 8; // Swym event type 8 = back-in-stock alert.
          this.swat.addToWatchList(event, params);
        });
      }

      render() {
        const total = this.totalCount;
        const tiers = this.tiers;
        const achieved = this.achievedTier(total);
        const next = this.nextTier(total);

        const progressText = this.querySelector('[data-mm-progress-text]');
        const progressHint = this.querySelector('[data-mm-progress-hint]');
        const progressBar = this.querySelector('[data-mm-progressbar]');
        const fill = this.querySelector('[data-mm-fill]');
        const subtotal = this.querySelector('[data-mm-subtotal]');
        const submit = this.querySelector('[data-mm-submit]');
        const submitText = this.querySelector('[data-mm-submit-text]');
        const bar = this.querySelector('[data-mm-bar]');

        const status = this.fillTemplate(this.dataset.textProgress, {
          count: total,
          total: next ? next.size : total,
        });
        if (progressText) progressText.textContent = status;

        // Hint always points at the next reward; once past the largest pack
        // there is nothing left to climb, so celebrate what was reached.
        let hint = '';
        if (next && total > 0) {
          hint = this.fillTemplate(this.dataset.textRemaining, { count: next.size - total, tier: next.label });
        } else if (!next && achieved) {
          hint = this.fillTemplate(this.dataset.textComplete, { tier: achieved.label, count: total });
        }
        if (progressHint) {
          progressHint.textContent = hint;
          progressHint.hidden = hint === '';
        }

        if (progressBar && fill) {
          // Progress runs toward the next milestone; full once past the largest.
          const target = next ? next.size : null;
          const percent = target ? Math.min(100, Math.round((total / target) * 100)) : total > 0 ? 100 : 0;
          fill.style.width = `${percent}%`;
          progressBar.setAttribute('aria-valuenow', total);
          progressBar.setAttribute('aria-valuemax', target || Math.max(total, 1));
        }

        if (subtotal) subtotal.textContent = total > 0 ? this.formatMoney(this.subtotalCents) : '';

        if (bar) {
          bar.classList.toggle('mix-match-bar--active', total > 0);
          bar.classList.toggle('mix-match-bar--complete', achieved !== null);
        }

        // Highlight every milestone reached — the ladder the customer is climbing.
        tiers.forEach((tier) => {
          const reached = total >= tier.size;
          tier.el.classList.toggle('mix-match-tier--reached', reached);
          tier.el.classList.toggle('mix-match-tier--current', achieved !== null && tier.size === achieved.size);
          const state = tier.el.querySelector('[data-mm-tier-state]');
          if (state) state.hidden = !reached;
        });

        if (submit && submitText) {
          // Any non-empty basket can check out now; tiers only change the wording.
          submitText.textContent = achieved
            ? this.fillTemplate(this.dataset.textAdd, { tier: achieved.label, count: total })
            : this.fillTemplate(this.dataset.textAddFree, { count: total });
          submit.disabled = total === 0 || this.busy === true;
        }

        this.renderTray();
        this.syncCards();

        const live = this.querySelector('[data-mm-live]');
        if (live) live.textContent = hint ? `${status}. ${hint}` : status;
      }

      renderTray() {
        const tray = this.querySelector('[data-mm-tray]');
        if (!tray) return;
        tray.textContent = '';

        this.selections.forEach((entry, variantId) => {
          const item = document.createElement('span');
          item.className = 'mix-match-tray__item';

          if (entry.image) {
            const img = document.createElement('img');
            img.src = entry.image;
            img.alt = entry.title || '';
            img.width = 40;
            img.height = 40;
            img.loading = 'lazy';
            item.appendChild(img);
          }

          const count = document.createElement('span');
          count.className = 'mix-match-tray__count badge';
          count.textContent = `×${entry.qty}`;
          item.appendChild(count);

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'mix-match-tray__remove';
          remove.setAttribute('data-mm-remove', '');
          remove.dataset.variantId = variantId;
          const removeName = entry.variantTitle ? `${entry.title} — ${entry.variantTitle}` : entry.title || '';
          remove.setAttribute('aria-label', this.fillTemplate(this.dataset.textRemove, { title: removeName }));
          remove.textContent = '×';
          item.appendChild(remove);

          const name = document.createElement('span');
          name.className = 'visually-hidden';
          name.textContent = removeName;
          item.appendChild(name);

          tray.appendChild(item);
        });
      }

      /* --- search filter --- */

      filterCards(query) {
        const term = (query || '').trim().toLowerCase();
        let visible = 0;
        this.querySelectorAll('[data-mm-card]').forEach((card) => {
          const match = !term || (card.dataset.search || '').includes(term);
          card.hidden = !match;
          if (match) visible += 1;
        });
        const empty = this.querySelector('[data-mm-no-results]');
        if (empty) empty.hidden = visible > 0;
      }

      /* --- filters (server-side via Section Rendering API) --- */

      applyFilters() {
        const params = new URLSearchParams();
        this.querySelectorAll('[data-mm-filter-chip].mix-match-chip--active').forEach((chip) => {
          params.append(chip.dataset.paramName, chip.dataset.value);
        });
        // Sort travels with the filters so changing one never silently drops
        // the other. Omitted entirely when unset, which leaves the collection's
        // own default sort in charge.
        const sort = this.querySelector('[data-mm-sort]');
        if (sort && sort.value) params.set('sort_by', sort.value);
        this.refreshCatalog(params, true);
      }

      refreshCatalog(params, pushState) {
        const catalog = this.querySelector('[data-mm-catalog]');
        if (!catalog || !this.dataset.collectionUrl) return;
        this.classList.add('mix-match--loading');
        const qs = params.toString();
        const url = `${this.dataset.collectionUrl}?${qs ? `${qs}&` : ''}section_id=${this.dataset.sectionId}`;

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            const fresh = doc.querySelector('[data-mm-catalog]');
            if (fresh) catalog.replaceWith(document.importNode(fresh, true));

            // A new filter selection replaces the catalog with page 1.
            this.loadedPages = 1;

            const search = this.querySelector('[data-mm-search]');
            if (search && search.value) this.filterCards(search.value);
            this.syncCards();

            const live = this.querySelector('[data-mm-live]');
            const count = this.querySelector('[data-mm-result-count]');
            if (live && count) live.textContent = count.textContent.trim();

            if (pushState) {
              const pageUrl = `${this.dataset.collectionUrl}${qs ? `?${qs}` : ''}`;
              history.pushState({ mmFilters: qs }, '', pageUrl);
            }
          })
          .catch((error) => console.error(error))
          .finally(() => this.classList.remove('mix-match--loading'));
      }

      /* --- load more (Section Rendering API) --- */

      loadMore(button) {
        if (button.classList.contains('loading')) return;
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(button.dataset.nextUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            const grid = this.querySelector('[data-mm-grid]');
            doc.querySelectorAll('[data-mm-grid] > *').forEach((card) => {
              grid.appendChild(document.importNode(card, true));
            });

            const nextButton = doc.querySelector('[data-mm-load-more]');
            if (nextButton) {
              button.dataset.nextUrl = nextButton.dataset.nextUrl;
            } else {
              button.closest('.mix-match__load-more').hidden = true;
            }

            this.loadedPages += 1;

            const search = this.querySelector('[data-mm-search]');
            if (search && search.value) this.filterCards(search.value);
            this.syncCards();
          })
          .catch((error) => console.error(error))
          .finally(() => {
            button.classList.remove('loading');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      /* --- add to cart --- */

      showError(message) {
        const error = this.querySelector('[data-mm-error]');
        if (!error) return;
        error.hidden = !message;
        error.textContent = message || '';
      }

      addBundleToCart(button) {
        if (this.busy || button.disabled) return;
        this.busy = true;
        this.showError('');
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        // Attribution: underscore-prefixed line item properties are hidden from
        // the customer by the theme's cart templates but persist onto the order
        // (and abandoned checkouts), so mix-match sales are measurable via the
        // Admin API. See scripts/mm-report.mjs.
        // Record the tier actually *reached*, never a pre-selected goal —
        // otherwise a 30-can order lands in the 6-pack cohort and corrupts the
        // tier breakdown in scripts/mm-metrics.mjs and the mm-<tier> order tag.
        // Below the smallest pack size there is no tier, so it stays 'custom'.
        const bundleSize = this.totalCount;
        const achieved = this.achievedTier(bundleSize);
        const bundleLabel = achieved ? achieved.label : 'custom';
        const items = [];
        this.selections.forEach((entry, variantId) => {
          items.push({
            id: parseInt(variantId, 10),
            quantity: entry.qty,
            properties: { _mm_bundle: bundleLabel, _mm_bundle_size: String(bundleSize) },
          });
        });

        const body = {
          items,
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [],
          sections_url: window.location.pathname,
        };
        if (this.cart && this.cart.setActiveElement) this.cart.setActiveElement(document.activeElement);

        fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: JSON.stringify(body) })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'mix-match-builder',
                errors: response.errors || response.description,
                message: response.message,
              });
              this.showError(response.description || response.message || this.dataset.textError);
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mix-match-builder', cartData: response });

            // Cart-level attribution flag (fire-and-forget): becomes an order
            // note-attribute / abandoned-checkout attribute for quick filtering.
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({ attributes: { mm_bundle: bundleLabel, mm_bundle_size: String(bundleSize) } }),
            }).catch(() => {});

            this.selections.clear();
            this.persist();
            this.render();

            if (this.cart) {
              // The add succeeded — never surface an error past this point.
              // response.sections can come back null (e.g. when the request
              // crossed a domain redirect); re-fetch the section HTML then.
              const renderDrawer = (state) => {
                try {
                  this.cart.renderContents(state);
                  if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                } catch (error) {
                  console.error(error);
                }
              };
              if (response.sections) {
                renderDrawer(response);
              } else {
                const ids = this.cart.getSectionsToRender().map((section) => section.id);
                fetch(`${window.location.pathname}?sections=${ids.join(',')}`)
                  .then((r) => r.json())
                  .then((sections) => renderDrawer({ ...response, sections }))
                  .catch(() => {});
              }
            } else {
              window.location = routes.cart_url;
            }
          })
          .catch((error) => {
            console.error(error);
            this.showError(this.dataset.textError);
          })
          .finally(() => {
            this.busy = false;
            button.classList.remove('loading');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
            this.render();
          });
      }
    }
  );
}
